import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { Container } from './ui/Container'
import { Card, CardContent, CardHeader, CardTitle } from './ui/Card'
import { Button } from './ui/Button'
import { toast } from 'sonner'
import {
  Pill,
  Plus,
  CheckCircle,
  Clock,
  AlertTriangle,
  TrendingUp,
  Calendar,
  User,
  Filter,
  Search,
  ChevronRight
} from 'lucide-react'

interface Supplement {
  id: string
  name: string
  description: string
  benefits: string[]
  dosage: string
  timing: string
  warnings: string[]
  category: 'vitamin' | 'mineral' | 'herb' | 'probiotic' | 'omega' | 'protein'
  form: 'capsule' | 'tablet' | 'powder' | 'liquid' | 'gummy'
  image: string
  interactions: string[]
  effectiveness: number
}

const supplementData: Supplement[] = [
  {
    id: '1',
    name: 'Longevity Mix',
    description: 'Comprehensive longevity formula with evidence-based compounds',
    benefits: ['Cellular health', 'DNA repair', 'Metabolic optimization', 'Aging support'],
    dosage: 'As directed on label',
    timing: 'With morning meal',
    warnings: ['Consult healthcare provider before use', 'Not for pregnant or nursing women'],
    category: 'vitamin',
    form: 'capsule',
    image: 'https://images.unsplash.com/photo-1555633514-ab08830f7dc6?auto=format&fit=crop&q=80&w=800&aspect_ratio=1:1',
    interactions: [],
    effectiveness: 95
  },
  {
    id: '2',
    name: 'Creatine',
    description: 'Pure creatine monohydrate for muscle performance and cognitive function',
    benefits: ['Muscle strength', 'Exercise performance', 'Cognitive support', 'Energy production'],
    dosage: '5g daily',
    timing: 'With carbohydrates for enhanced absorption',
    warnings: ['Maintain adequate hydration', 'Consult doctor if kidney issues'],
    category: 'protein',
    form: 'powder',
    image: 'https://images.unsplash.com/photo-1593095948071-474c5cc2989d?auto=format&fit=crop&q=80&w=800&aspect_ratio=1:1',
    interactions: [],
    effectiveness: 90
  },
  {
    id: '3',
    name: 'Ashwagandha + Rhodiola',
    description: 'Adaptogenic blend for stress resilience and mental clarity',
    benefits: ['Stress reduction', 'Mental clarity', 'Cortisol balance', 'Energy support'],
    dosage: '500-1000mg daily',
    timing: 'Morning with food',
    warnings: ['May interact with thyroid medications', 'Start with lower dose'],
    category: 'herb',
    form: 'capsule',
    image: 'https://images.unsplash.com/photo-1611079830811-865ec446a617?auto=format&fit=crop&q=80&w=800&aspect_ratio=1:1',
    interactions: ['Thyroid medications', 'Sedatives'],
    effectiveness: 88
  },
  {
    id: '4',
    name: 'NAC + Ginger + Curcumin',
    description: 'Anti-inflammatory complex with antioxidant support',
    benefits: ['Antioxidant support', 'Inflammation modulation', 'Liver health', 'Immune function'],
    dosage: '600-1200mg daily',
    timing: 'Between meals for best absorption',
    warnings: ['May cause mild stomach upset', 'Avoid if allergic to nightshades'],
    category: 'herb',
    form: 'capsule',
    image: 'https://images.unsplash.com/photo-1616671276441-2f2c277b8bf8?auto=format&fit=crop&q=80&w=800&aspect_ratio=1:1',
    interactions: [],
    effectiveness: 85
  },
  {
    id: '5',
    name: 'Essential Capsules',
    description: 'Daily essential vitamins and minerals in bioavailable forms',
    benefits: ['Daily nutrition', 'Energy metabolism', 'Immune support', 'Cellular health'],
    dosage: '2-4 capsules daily',
    timing: 'With breakfast',
    warnings: ['Take with food to prevent stomach upset', 'Monitor total vitamin intake'],
    category: 'vitamin',
    form: 'capsule',
    image: 'https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?auto=format&fit=crop&q=80&w=800&aspect_ratio=1:1',
    interactions: [],
    effectiveness: 90
  },
  {
    id: '6',
    name: 'Advanced Antioxidants',
    description: 'High-potency antioxidant complex for cellular protection',
    benefits: ['Cellular protection', 'Oxidative stress defense', 'Skin health', 'Aging support'],
    dosage: '1-2 capsules daily',
    timing: 'With meals for optimal absorption',
    warnings: ['May interact with chemotherapy', 'Consult healthcare provider'],
    category: 'vitamin',
    form: 'capsule',
    image: 'https://images.unsplash.com/photo-1471357674240-e1a485acb3e1?auto=format&fit=crop&q=80&w=800&aspect_ratio=1:1',
    interactions: [],
    effectiveness: 88
  },
  {
    id: '7',
    name: 'Collagen',
    description: 'Hydrolyzed collagen peptides for skin, joint, and bone health',
    benefits: ['Skin elasticity', 'Joint health', 'Bone strength', 'Hair and nails'],
    dosage: '10-20g daily',
    timing: 'Morning in coffee or smoothie',
    warnings: ['Contains animal products', 'May cause mild digestive upset'],
    category: 'protein',
    form: 'powder',
    image: 'https://images.unsplash.com/photo-1544966445-585770013d65?auto=format&fit=crop&q=80&w=800&aspect_ratio=1:1',
    interactions: [],
    effectiveness: 85
  }
]

const categories = [
  { id: 'all', name: 'All Supplements', icon: Pill },
  { id: 'vitamin', name: 'Vitamins', icon: Pill },
  { id: 'mineral', name: 'Minerals', icon: Pill },
  { id: 'omega', name: 'Omega-3', icon: Pill },
  { id: 'probiotic', name: 'Probiotics', icon: Pill },
  { id: 'herb', name: 'Herbs', icon: Pill },
  { id: 'protein', name: 'Protein', icon: Pill }
]

const SupplementSection: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedSupplement, setSelectedSupplement] = useState<Supplement | null>(null)

  const handleComingSoon = (feature: string) => {
    toast.info(`🚀 ${feature} - Feature Coming Soon!`, {
      duration: 3000,
      position: 'top-center',
    });
  };

  const filteredSupplements = supplementData.filter(supplement => {
    const matchesCategory = selectedCategory === 'all' || supplement.category === selectedCategory
    const matchesSearch = supplement.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           supplement.description.toLowerCase().includes(searchTerm.toLowerCase())
    return matchesCategory && matchesSearch
  })

  // const getCategoryColor = (category: string) => {
  //   const colors = {
  //     vitamin: 'from-amber-500 to-orange-600',
  //     mineral: 'from-green-500 to-emerald-600',
  //     omega: 'from-blue-500 to-cyan-600',
  //     probiotic: 'from-pink-500 to-rose-600',
  //     herb: 'from-purple-500 to-indigo-600',
  //     protein: 'from-red-500 to-pink-600'
  //   }
  //   return colors[category as keyof typeof colors] || 'from-gray-500 to-gray-600'
  // }

  return (
    <section className="py-20 bg-theme-bg content-section">
      <Container>
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-600 to-teal-500 rounded-2xl mb-6">
              <Pill className="w-8 h-8 text-theme-text-inverse" />
            </div>
            <h2 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-blue-500 to-teal-400 bg-clip-text text-transparent gradient-heading mb-6">
              Nobody Else Operates Like We Do
            </h2>
            <p className="text-xl text-theme-secondary max-w-3xl mx-auto leading-relaxed mb-8">
              Today's supplement industry is flooded with empty promises and hidden formulas. We dismantle the deception by testing every batch, publishing every finding, and making radical transparency our foundation.
            </p>
            
            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto mb-12">
              <Card className="bg-theme-surface/80 border-theme-border overflow-hidden hover:shadow-lg transition-all duration-300 group">
                <div className="h-40 overflow-hidden relative">
                  <div className="absolute inset-0 bg-theme-danger/20 z-10 group-hover:bg-theme-danger/10 transition-colors"></div>
                  <img 
                    src="https://images.unsplash.com/photo-1532187863486-abf9dbad1b69?auto=format&fit=crop&q=80&w=600" 
                    alt="Lab testing" 
                    className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-4 right-4 z-20 bg-theme-danger text-white w-8 h-8 rounded-full flex items-center justify-center font-bold">✕</div>
                </div>
                <CardContent className="p-6 text-center">
                  <h3 className="text-lg font-bold text-theme-danger mb-2">ZERO PROPRIETARY BLENDS</h3>
                  <p className="text-sm text-theme-secondary">We expose every milligram - no masking behind secret formulas. You know exactly what you're putting in your body.</p>
                </CardContent>
              </Card>
              
              <Card className="bg-theme-surface/80 border-theme-border overflow-hidden hover:shadow-lg transition-all duration-300 group">
                <div className="h-40 overflow-hidden relative">
                  <div className="absolute inset-0 bg-theme-danger/20 z-10 group-hover:bg-theme-danger/10 transition-colors"></div>
                   <img 
                    src="https://images.unsplash.com/photo-1550989460-0adf9ea622e2?auto=format&fit=crop&q=80&w=600" 
                    alt="Precision dosing" 
                    className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-4 right-4 z-20 bg-theme-danger text-white w-8 h-8 rounded-full flex items-center justify-center font-bold">✕</div>
                </div>
                <CardContent className="p-6 text-center">
                  <h3 className="text-lg font-bold text-theme-danger mb-2">NO SPRINKLE DOSING</h3>
                  <p className="text-sm text-theme-secondary">We use clinical amounts shown to work in studies, not trace amounts just for label decoration.</p>
                </CardContent>
              </Card>
              
              <Card className="bg-theme-surface/80 border-theme-border overflow-hidden hover:shadow-lg transition-all duration-300 group">
                <div className="h-40 overflow-hidden relative">
                  <div className="absolute inset-0 bg-theme-danger/20 z-10 group-hover:bg-theme-danger/10 transition-colors"></div>
                   <img 
                    src="https://images.unsplash.com/photo-1587854692152-cbe660dbde88?auto=format&fit=crop&q=80&w=600" 
                    alt="Pure ingredients" 
                    className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-4 right-4 z-20 bg-theme-danger text-white w-8 h-8 rounded-full flex items-center justify-center font-bold">✕</div>
                </div>
                <CardContent className="p-6 text-center">
                  <h3 className="text-lg font-bold text-theme-danger mb-2">ZERO COMPROMISE</h3>
                  <p className="text-sm text-theme-secondary">Verified potency, zero fillers, absolute purity. We test raw materials and finished goods.</p>
                </CardContent>
              </Card>
            </div>
            
            <p className="text-lg text-theme-secondary max-w-2xl mx-auto leading-relaxed">
              Receive personalized supplement protocols built from your biomarker data and health objectives. Our intelligence engine calculates optimal compounds, timing, and quantities for your biology.
            </p>
          </motion.div>
        </div>

        {/* Search and Filter */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          viewport={{ once: true }}
          className="mb-12"
        >
          <div className="flex flex-col md:flex-row gap-4 mb-8">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-theme-secondary" />
              <input
                type="text"
                placeholder="Search supplements..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-theme-surface border border-theme-border rounded-xl text-theme-text placeholder-theme-secondary focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            <div className="flex items-center space-x-2">
              <Filter className="w-5 h-5 text-theme-secondary" />
              <span className="text-theme-secondary">Filter:</span>
            </div>
          </div>

          {/* Category Filter */}
          <div className="flex flex-wrap gap-2">
            {categories.map((category) => {
              const Icon = category.icon
              return (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all duration-200 ${
                    selectedCategory === category.id
                      ? 'bg-primary-600 text-theme-text-inverse'
                      : 'bg-theme-bg border border-theme-border text-theme-text hover:bg-theme-bg-alt'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{category.name}</span>
                </button>
              )
            })}
          </div>
        </motion.div>

        {/* Supplements Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {filteredSupplements.map((supplement, index) => (
            <motion.div
              key={supplement.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <Card className="h-full flex flex-col overflow-hidden hover:shadow-lg transition-all duration-300 group cursor-pointer bg-theme-surface/70 border border-theme-border hover:border-theme-primary">
                <CardHeader className="pb-4">
                  <div className="flex items-center justify-between mb-4">
                    <div className={`w-12 h-12 bg-theme-surface/70 rounded-xl flex items-center justify-center border border-theme-border`}>
                      <Pill className="w-6 h-6 text-theme-primary" />
                    </div>
                    <div className="flex items-center space-x-1">
                      <TrendingUp className="w-4 h-4 text-theme-success" />
                      <span className="text-sm font-semibold text-theme-success">{supplement.effectiveness}%</span>
                    </div>
                  </div>
                  <CardTitle className="text-lg text-theme-text group-hover:text-theme-primary transition-colors">
                    {supplement.name}
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex flex-col h-full">
                  <p className="text-theme-text-secondary group-hover:text-theme-primary text-sm mb-4 line-clamp-2">
                    {supplement.description}
                  </p>
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center space-x-2 text-sm">
                      <Clock className="w-4 h-4 text-theme-warning" />
                      <span className="text-theme-secondary">{supplement.timing}</span>
                    </div>
                    <div className="flex items-center space-x-2 text-sm">
                      <Calendar className="w-4 h-4 text-theme-primary" />
                      <span className="text-theme-secondary">{supplement.dosage}</span>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1 mb-4">
                    {supplement.benefits.slice(0, 3).map((benefit, idx) => (
                      <span key={idx} className="px-2 py-1 bg-theme-primary/20 text-theme-text text-xs rounded-full border border-theme-primary">
                        {benefit}
                      </span>
                    ))}
                  </div>
                  <div className="mt-auto">
                    <Button 
                      size="sm" 
                      className="w-full bg-gradient-to-r from-blue-600 to-teal-600 text-theme-text-inverse hover:from-blue-700 hover:to-teal-700"
                      onClick={() => setSelectedSupplement(supplement)}
                    >
                      Learn More
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Supplement Detail Modal */}
        {selectedSupplement && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedSupplement(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="bg-theme-surface/90 rounded-2xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-theme-border"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-4">
                  <div className={`w-16 h-16 bg-gradient-to-br from-blue-600 to-teal-500 rounded-xl flex items-center justify-center`}>
                    <Pill className="w-8 h-8 text-theme-text-inverse" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-theme-primary">{selectedSupplement.name}</h3>
                    <p className="text-theme-secondary">{selectedSupplement.description}</p>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedSupplement(null)}
                  className="text-theme-secondary hover:text-theme-text text-2xl"
                >
                  ×
                </button>
              </div>

              <div className="space-y-6">
                <div>
                  <h4 className="font-semibold text-theme-primary mb-2 flex items-center">
                    <TrendingUp className="w-4 h-4 mr-2 text-theme-success" />
                    Key Benefits
                  </h4>
                  <div className="grid grid-cols-2 gap-2">
                    {selectedSupplement.benefits.map((benefit, idx) => (
                      <div key={idx} className="flex items-center space-x-2">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        <span className="text-theme-secondary">{benefit}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold text-theme-primary mb-2 flex items-center">
                      <Calendar className="w-4 h-4 mr-2 text-theme-primary" />
                      Recommended Dosage
                    </h4>
                    <p className="text-theme-secondary">{selectedSupplement.dosage}</p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-theme-primary mb-2 flex items-center">
                      <Clock className="w-4 h-4 mr-2 text-theme-warning" />
                      Best Timing
                    </h4>
                    <p className="text-theme-secondary">{selectedSupplement.timing}</p>
                  </div>
                </div>

                {selectedSupplement.warnings.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-theme-primary mb-2 flex items-center">
                      <AlertTriangle className="w-4 h-4 mr-2 text-amber-500" />
                      Important Warnings
                    </h4>
                    <ul className="space-y-1">
                      {selectedSupplement.warnings.map((warning, idx) => (
                        <li key={idx} className="text-theme-warning text-sm">
                          • {warning}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {selectedSupplement.interactions.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-theme-primary mb-2 flex items-center">
                      <AlertTriangle className="w-4 h-4 mr-2 text-theme-danger" />
                      Drug Interactions
                    </h4>
                    <ul className="space-y-1">
                      {selectedSupplement.interactions.map((interaction, idx) => (
                        <li key={idx} className="text-theme-danger text-sm">
                          • {interaction}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="flex space-x-4">
                  <Button className="flex-1">
                    Add to My Supplements
                    <Plus className="w-4 h-4 ml-2" />
                  </Button>
                  <Button variant="outline" className="flex-1">
                    Consult Expert
                    <User className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          viewport={{ once: true }}
          className="text-center bg-theme-surface/80 border-2 border-theme-border rounded-2xl p-8 text-theme-text"
        >
          <div className="w-16 h-16 mx-auto mb-6 bg-gradient-to-br from-blue-600 to-teal-500 rounded-2xl flex items-center justify-center">
            <Pill className="w-8 h-8 text-theme-text-inverse" />
          </div>
          <h3 className="text-3xl font-bold mb-4 text-theme-primary">Every Formula is Engineered for Your Biology</h3>
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto mb-8">
            <div className="text-center">
              <div className="w-12 h-12 mx-auto mb-4 bg-theme-primary rounded-xl flex items-center justify-center">
                <span className="text-theme-text-inverse font-bold">📋</span>
              </div>
              <h4 className="font-bold text-theme-primary mb-2">FOUNDED ON LARGE-SCALE HUMAN DATA</h4>
              <p className="text-sm text-theme-secondary">Protocols derived from population research and clinical outcomes</p>
            </div>
            
            <div className="text-center">
              <div className="w-12 h-12 mx-auto mb-4 bg-theme-primary rounded-xl flex items-center justify-center">
                <span className="text-theme-text-inverse font-bold">🧪</span>
              </div>
              <h4 className="font-bold text-theme-primary mb-2">EACH COMPOUND EARNED ITS PLACE™</h4>
              <p className="text-sm text-theme-secondary">Multi-phase testing eliminates weak ingredients before they reach you</p>
            </div>
            
            <div className="text-center">
              <div className="w-12 h-12 mx-auto mb-4 bg-theme-primary rounded-xl flex items-center justify-center">
                <span className="text-theme-text-inverse font-bold">⚕️</span>
              </div>
              <h4 className="font-bold text-theme-primary mb-2">DESIGNED BY LONGEVITY PHYSICIANS AND RESEARCHERS</h4>
              <p className="text-sm text-theme-secondary">Medical experts and aging specialists formulate every product</p>
            </div>
          </div>
          <p className="text-xl mb-8 text-theme-secondary max-w-2xl mx-auto">
            Receive AI-driven supplement protocols calibrated to your biomarker profile and health objectives.
          </p>
          <Button 
            size="lg" 
            className="bg-gradient-to-r from-blue-600 to-teal-500 text-theme-text-inverse hover:from-blue-700 hover:to-teal-600"
            onClick={() => handleComingSoon('Supplement Plan')}
          >
            Create My Plan
            <ChevronRight className="w-5 h-5 ml-2" />
          </Button>
        </motion.div>
      </Container>
    </section>
  )
}

export default SupplementSection
